// FichaClienteModal.js — ficha completa del cliente (Prioridad 1.4): contacto,
// dirección, activos, contratos y su estado, historial de órdenes e importe
// pendiente de cobro.
// "Detalles de acceso"/"observaciones" editables por el técnico se quitaron
// de aquí por decisión de dirección (2026-10-05, Parte 1 punto 2): casi nadie
// los rellenaba y no se quería que ningún técnico los pidiera/editara. Los
// datos que ya hubiera en Firestore (campo detallesAcceso/observacionesTecnico
// en clientes) se quedan sin usar, no se borran — ver getFichaCliente.js.
import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../../services/authService';

function fmtEur(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
function fmtFecha(f) {
  if (!f) return '—';
  const [y, m, d] = String(f).slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : f;
}

export default function FichaClienteModal({ clienteId, ordenId, onClose }) {
  const [ficha, setFicha] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    const params = new URLSearchParams({ clienteId, ...(ordenId ? { ordenId } : {}) });
    fetch(`/.netlify/functions/getFichaCliente?${params}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => {
        if (!data.success) { setError(data.error); return; }
        setFicha(data);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [clienteId, ordenId]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-xl w-full sm:max-w-lg max-h-[88vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b">
          <h3 className="font-bold text-gray-900">Ficha del cliente</h3>
          <button data-attr="ficha-cliente-cerrar" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1">×</button>
        </div>

        <div className="ph-no-capture flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500" /></div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

          {!loading && ficha && (
            <>
              <div>
                <p className="font-bold text-gray-900 text-lg">{ficha.cliente.nombre}</p>
                <div className="text-sm text-gray-600 mt-1 space-y-0.5">
                  {ficha.cliente.telefono && <p>📞 {ficha.cliente.telefono}{ficha.cliente.telefono2 ? ` · ${ficha.cliente.telefono2}` : ''}</p>}
                  {ficha.cliente.email && <p>✉️ {ficha.cliente.email}</p>}
                  {ficha.cliente.direccion && <p>📍 {ficha.cliente.direccion}{ficha.cliente.municipio ? `, ${ficha.cliente.municipio}` : ''} {ficha.cliente.codigoPostal || ''}</p>}
                </div>
              </div>

              {ficha.importePendiente > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800">
                  ⚠️ {fmtEur(ficha.importePendiente)} pendiente de cobro ({ficha.facturasPendientesCount} factura{ficha.facturasPendientesCount !== 1 ? 's' : ''})
                </div>
              )}

              {ficha.activos.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Activos ({ficha.activos.length})</p>
                  <div className="space-y-1.5">
                    {ficha.activos.map(a => (
                      <div key={a.id} className="bg-gray-50 rounded-xl px-3 py-2 text-sm">
                        <p className="font-semibold text-gray-800">{[a.marca, a.modelo].filter(Boolean).join(' ') || 'Equipo'}</p>
                        {a.numeroSerie && <p className="text-xs text-gray-400 font-mono">Nº serie: {a.numeroSerie}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ficha.contratos.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Contratos</p>
                  <div className="space-y-1.5">
                    {ficha.contratos.map(c => (
                      <div key={c.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2 text-sm">
                        <span className="font-semibold text-gray-800">{c.pack || 'Mantenimiento'}</span>
                        <span className="text-xs text-gray-500">{c.estado || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ficha.ordenes.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Historial de órdenes</p>
                  <div className="space-y-1.5">
                    {ficha.ordenes.map(o => (
                      <div key={o.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2 text-sm">
                        <span className="text-gray-700">{o.referencia}</span>
                        <span className="text-xs text-gray-400">{fmtFecha(o.fecha)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
